// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * SaveLoadManager - Centralized Save/Load System
 * 
 * Coordinates saving and loading of all game state across contexts.
 * Provides auto-save, manual save/load, and version management.
 */

import STORAGE_KEYS from './STORAGE_KEYS';

const SAVE_VERSION = '1.1.0';
const SAVE_KEY_PREFIX = 'starship_console_save_slot';

/**
 * Migration functions keyed by the version they migrate FROM.
 * Each function receives the save state and returns the migrated state.
 * Add a new entry here whenever the save format changes.
 */
const MIGRATIONS = {
  '1.0.0': (state) => {
    // 1.0.0 → 1.1.0: no structural changes, version bump only.
    // Future migrations go here as new entries.
    return { ...state, version: '1.1.0' };
  }
};

/**
 * Apply all pending migrations to a loaded save state in version order.
 */
function migrateState(state) {
  let current = state;
  const fromVersions = Object.keys(MIGRATIONS).sort();

  for (const fromVersion of fromVersions) {
    if (current.version === fromVersion) {
      const migrated = MIGRATIONS[fromVersion](current);
      console.log(`[SaveLoad] Migrated save: ${fromVersion} → ${migrated.version}`);
      current = migrated;
    }
  }

  return current;
}
const AUTO_SAVE_KEY = 'starship_console_autosave'; // Separate auto-save slot
const CURRENT_SLOT_KEY = 'starship_console_current_slot';
const AUTO_SAVE_INTERVAL = parseInt(localStorage.getItem('autosaveInterval') || '60000', 10);
const MAX_SLOTS = 10;

class SaveLoadManager {
  constructor() {
    this.autoSaveTimer = null;
    this.currentSlot = this.getCurrentSlot();
  }

  /**
   * Get current active slot (1-5)
   */
  getCurrentSlot() {
    const saved = localStorage.getItem(CURRENT_SLOT_KEY);
    return saved ? parseInt(saved, 10) : 1;
  }

  /**
   * Set current active slot
   */
  setCurrentSlot(slotNumber) {
    if (slotNumber < 1 || slotNumber > MAX_SLOTS) {
      console.error(`[SaveLoad] Invalid slot number: ${slotNumber}`);
      return false;
    }
    this.currentSlot = slotNumber;
    localStorage.setItem(CURRENT_SLOT_KEY, slotNumber.toString());
    return true;
  }

  /**
   * Get save key for a specific slot
   */
  getSaveKey(slotNumber) {
    return `${SAVE_KEY_PREFIX}_${slotNumber}`;
  }

  /**
   * Get current game state from all localStorage keys
   */
  getCurrentState() {
    const state = {
      version: SAVE_VERSION,
      timestamp: Date.now(),
      
      // Fuel state
      fuel: {
        antimatterFuel: localStorage.getItem(STORAGE_KEYS.FUEL),
        maxFuelCapacity: localStorage.getItem(STORAGE_KEYS.MAX_FUEL_CAPACITY),
        tankUpgrades: localStorage.getItem(STORAGE_KEYS.TANK_UPGRADES),
      },

      // Credits state
      credits: localStorage.getItem(STORAGE_KEYS.CREDITS),

      // Weapons state
      weapons: {
        weapons: localStorage.getItem(STORAGE_KEYS.WEAPONS),
        unlockedWeaponSlots: localStorage.getItem(STORAGE_KEYS.UNLOCKED_WEAPON_SLOTS),
      },

      // Upgrades state
      upgrades: {
        purchasedUpgrades: localStorage.getItem(STORAGE_KEYS.PURCHASED_UPGRADES),
      },

      // Ship health state
      shipHealth: {
        shipSystems: localStorage.getItem(STORAGE_KEYS.SHIP_SYSTEMS),
      },

      // Cargo state
      cargo: {
        cargo: localStorage.getItem(STORAGE_KEYS.CARGO),
      },

      // Modules state
      modules: {
        installedModules: localStorage.getItem(STORAGE_KEYS.INSTALLED_MODULES),
      },

      // Navigation state (if exists)
      navigation: {
        currentLocation: localStorage.getItem(STORAGE_KEYS.CURRENT_LOCATION),
        selectedSystem: localStorage.getItem(STORAGE_KEYS.SELECTED_SYSTEM),
        inOrbit: localStorage.getItem(STORAGE_KEYS.IN_ORBIT),
        scannedSystems: localStorage.getItem(STORAGE_KEYS.SCANNED_SYSTEMS),
        discoveredSystems: localStorage.getItem(STORAGE_KEYS.DISCOVERED_SYSTEMS),
        storedScanResults: localStorage.getItem(STORAGE_KEYS.STORED_SCAN_RESULTS),
        navigationMode: localStorage.getItem(STORAGE_KEYS.NAVIGATION_MODE),
      },

      // Universe state (warp gates)
      universe: {
        universeState: localStorage.getItem(STORAGE_KEYS.UNIVERSE_STATE),
      },

      // Crew state
      crew: {
        crew: localStorage.getItem(STORAGE_KEYS.CREW),
      },

      // Captain's Log
      captainsLog: {
        log: localStorage.getItem(STORAGE_KEYS.CAPTAINS_LOG),
      },

      // Game Time
      gameTime: {
        time: localStorage.getItem(STORAGE_KEYS.GAME_TIME),
      },

      // Resource Manager
      resources: {
        resources: localStorage.getItem(STORAGE_KEYS.RESOURCES),
      },

      // Achievements
      achievements: {
        achievements: localStorage.getItem(STORAGE_KEYS.ACHIEVEMENTS),
      },

      // Ship Name
      shipName: {
        name: localStorage.getItem(STORAGE_KEYS.SHIP_NAME),
      },

      // Tutorial State
      tutorial: {
        completed: localStorage.getItem(STORAGE_KEYS.TUTORIAL_COMPLETED),
      },

      // Voice preference
      voice: {
        shipVoice: localStorage.getItem(STORAGE_KEYS.SHIP_VOICE),
      },
    };
    
    return state;
  }

  /**
   * Save current game state to a specific slot
   * @param {number} slotNumber - Slot to save to (1-10), or null for auto-save
   * @param {string} slotName - Optional name for the save
   * @param {boolean} silent - If true, suppress console logging (for auto-save)
   */
  save(slotNumber = null, slotName = null, silent = false) {
    try {
      // Auto-save uses separate key, manual saves use slot numbers
      const isAutoSave = slotNumber === null && silent === true;
      const saveKey = isAutoSave ? AUTO_SAVE_KEY : this.getSaveKey(slotNumber || this.currentSlot);
      const slot = isAutoSave ? 'auto' : (slotNumber || this.currentSlot);
      
      const state = this.getCurrentState();
      
      // Add slot metadata
      state.slotNumber = slot;
      state.slotName = isAutoSave ? 'Auto-Save' : (slotName || this.getSlotName(slot) || `Save ${slot}`);
      state.isAutoSave = isAutoSave;
      
      localStorage.setItem(saveKey, JSON.stringify(state));
      
      // Update slot name if provided (manual saves only)
      if (slotName && !isAutoSave) {
        this.setSlotName(slot, slotName);
      }
      
      // Update current slot for manual saves
      if (!isAutoSave && slotNumber) {
        this.setCurrentSlot(slotNumber);
      }
      
      // Only log if not silent (manual saves log, auto-saves don't)
      if (!silent) {
        // Manual save completed
      }
      
      return { success: true, timestamp: state.timestamp, slot, slotName: state.slotName };
    } catch (error) {
      console.error('[SaveLoad] Failed to save game:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Load game state from a specific slot (or auto-save).
   * Always reloads the page on success — every context (Fuel, Credits,
   * Cargo, Weapons, Upgrades, ShipHealth, UniverseState) and
   * NavigationPanel's currentLocation/discoveredSystems/etc. read
   * localStorage exactly once, at mount, via useState(() => ...)
   * initializers. Writing new values to localStorage here does NOT make
   * any of them re-read — the only way already-mounted React state picks
   * up a freshly loaded save is a full reload. A previous `skipReload`
   * flag let API-triggered loads skip this, which silently broke restore
   * for most fields (notably currentLocation) while the write itself
   * succeeded — see FINDINGS-style bug report, 2026-10-07 session.
   */
  load(slotNumber = null) {
    try {
      // Handle auto-save slot
      const isAutoSave = slotNumber === 'auto';
      const saveKey = isAutoSave ? AUTO_SAVE_KEY : this.getSaveKey(slotNumber || this.currentSlot);
      const slot = slotNumber || this.currentSlot;
      
      const savedData = localStorage.getItem(saveKey);
      
      if (!savedData) {
        return { success: false, error: 'No save data found' };
      }

      let state = JSON.parse(savedData);

      // Migrate older saves to current format before restoring
      if (state.version !== SAVE_VERSION) {
        state = migrateState(state);
      }

      // Restore all state to localStorage
      // Fuel
      if (state.fuel.antimatterFuel) localStorage.setItem(STORAGE_KEYS.FUEL, state.fuel.antimatterFuel);
      if (state.fuel.maxFuelCapacity) localStorage.setItem(STORAGE_KEYS.MAX_FUEL_CAPACITY, state.fuel.maxFuelCapacity);
      if (state.fuel.tankUpgrades) localStorage.setItem(STORAGE_KEYS.TANK_UPGRADES, state.fuel.tankUpgrades);

      // Credits
      if (state.credits) localStorage.setItem(STORAGE_KEYS.CREDITS, state.credits);

      // Weapons
      if (state.weapons.weapons) localStorage.setItem(STORAGE_KEYS.WEAPONS, state.weapons.weapons);
      if (state.weapons.unlockedWeaponSlots) localStorage.setItem(STORAGE_KEYS.UNLOCKED_WEAPON_SLOTS, state.weapons.unlockedWeaponSlots);

      // Upgrades
      if (state.upgrades.purchasedUpgrades) localStorage.setItem(STORAGE_KEYS.PURCHASED_UPGRADES, state.upgrades.purchasedUpgrades);

      // Ship health
      if (state.shipHealth.shipSystems) localStorage.setItem(STORAGE_KEYS.SHIP_SYSTEMS, state.shipHealth.shipSystems);

      // Cargo
      if (state.cargo.cargo) localStorage.setItem(STORAGE_KEYS.CARGO, state.cargo.cargo);

      // Modules
      if (state.modules.installedModules) localStorage.setItem(STORAGE_KEYS.INSTALLED_MODULES, state.modules.installedModules);

      // Navigation
      if (state.navigation.currentLocation) localStorage.setItem(STORAGE_KEYS.CURRENT_LOCATION, state.navigation.currentLocation);
      if (state.navigation.selectedSystem) localStorage.setItem(STORAGE_KEYS.SELECTED_SYSTEM, state.navigation.selectedSystem);
      if (state.navigation.inOrbit) localStorage.setItem(STORAGE_KEYS.IN_ORBIT, state.navigation.inOrbit);
      if (state.navigation.scannedSystems) localStorage.setItem(STORAGE_KEYS.SCANNED_SYSTEMS, state.navigation.scannedSystems);
      if (state.navigation.discoveredSystems) localStorage.setItem(STORAGE_KEYS.DISCOVERED_SYSTEMS, state.navigation.discoveredSystems);
      if (state.navigation.storedScanResults) localStorage.setItem(STORAGE_KEYS.STORED_SCAN_RESULTS, state.navigation.storedScanResults);
      if (state.navigation.navigationMode) localStorage.setItem(STORAGE_KEYS.NAVIGATION_MODE, state.navigation.navigationMode);

      // Universe state (warp gates)
      if (state.universe && state.universe.universeState) {
        localStorage.setItem(STORAGE_KEYS.UNIVERSE_STATE, state.universe.universeState);
      }

      // Crew
      if (state.crew && state.crew.crew) {
        localStorage.setItem(STORAGE_KEYS.CREW, state.crew.crew);
      }

      // Captain's Log
      if (state.captainsLog && state.captainsLog.log) {
        localStorage.setItem(STORAGE_KEYS.CAPTAINS_LOG, state.captainsLog.log);
      }

      // Game Time
      if (state.gameTime && state.gameTime.time) {
        localStorage.setItem(STORAGE_KEYS.GAME_TIME, state.gameTime.time);
      }

      // Resource Manager
      if (state.resources && state.resources.resources) {
        localStorage.setItem(STORAGE_KEYS.RESOURCES, state.resources.resources);
      }

      // Achievements
      if (state.achievements && state.achievements.achievements) {
        localStorage.setItem(STORAGE_KEYS.ACHIEVEMENTS, state.achievements.achievements);
      }

      // Ship Name
      if (state.shipName && state.shipName.name) {
        localStorage.setItem(STORAGE_KEYS.SHIP_NAME, state.shipName.name);
      }

      // Tutorial State
      if (state.tutorial && state.tutorial.completed) {
        localStorage.setItem(STORAGE_KEYS.TUTORIAL_COMPLETED, state.tutorial.completed);
      }

      // Voice preference
      if (state.voice && state.voice.shipVoice) {
        localStorage.setItem(STORAGE_KEYS.SHIP_VOICE, state.voice.shipVoice);
      }

      // Set this as the current slot
      this.setCurrentSlot(slot);

      // Always reload — see load()'s docstring for why skipReload was removed.
      window.location.reload();

      return { 
        success: true, 
        timestamp: state.timestamp,
        version: state.version,
        slot,
        slotName: state.slotName
      };
    } catch (error) {
      console.error('[SaveLoad] Failed to load game:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if save data exists in a slot
   */
  hasSaveData(slotNumber = null) {
    const slot = slotNumber || this.currentSlot;
    const saveKey = this.getSaveKey(slot);
    return localStorage.getItem(saveKey) !== null;
  }

  /**
   * Get save info for a specific slot without loading
   */
  getSaveInfo(slotNumber = null) {
    try {
      const slot = slotNumber || this.currentSlot;
      const saveKey = this.getSaveKey(slot);
      const savedData = localStorage.getItem(saveKey);
      
      if (!savedData) return null;

      const state = JSON.parse(savedData);
      return {
        slot,
        slotName: state.slotName || `Save ${slot}`,
        version: state.version,
        timestamp: state.timestamp,
        date: new Date(state.timestamp).toLocaleString(),
      };
    } catch (error) {
      console.error('[SaveLoad] Failed to get save info:', error);
      return null;
    }
  }

  /**
   * Get all save slots info (including auto-save)
   */
  getAllSaveSlots() {
    const slots = [];
    
    // Add auto-save slot first
    const autoSaveData = localStorage.getItem(AUTO_SAVE_KEY);
    if (autoSaveData) {
      try {
        const state = JSON.parse(autoSaveData);
        slots.push({
          slot: 'auto',
          isEmpty: false,
          slotName: 'Auto-Save',
          version: state.version,
          timestamp: state.timestamp,
          date: new Date(state.timestamp).toLocaleString(),
          isAutoSave: true
        });
      } catch (error) {
        console.error('[SaveLoad] Failed to parse auto-save:', error);
      }
    } else {
      slots.push({
        slot: 'auto',
        isEmpty: true,
        slotName: 'Auto-Save',
        isAutoSave: true
      });
    }
    
    // Add manual save slots
    for (let i = 1; i <= MAX_SLOTS; i++) {
      const info = this.getSaveInfo(i);
      slots.push({
        slot: i,
        isEmpty: !info,
        ...info
      });
    }
    return slots;
  }

  /**
   * Delete save data from a specific slot (or auto-save)
   */
  deleteSave(slotNumber = null) {
    try {
      const isAutoSave = slotNumber === 'auto';
      
      if (isAutoSave) {
        localStorage.removeItem(AUTO_SAVE_KEY);
        return { success: true, slot: 'auto' };
      }
      
      const slot = slotNumber || this.currentSlot;
      const saveKey = this.getSaveKey(slot);
      const nameKey = `${saveKey}_name`;
      
      localStorage.removeItem(saveKey);
      localStorage.removeItem(nameKey);
      
      return { success: true, slot };
    } catch (error) {
      console.error('[SaveLoad] Failed to delete save:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get slot name
   */
  getSlotName(slotNumber) {
    const nameKey = `${this.getSaveKey(slotNumber)}_name`;
    return localStorage.getItem(nameKey);
  }

  /**
   * Set slot name
   */
  setSlotName(slotNumber, name) {
    const nameKey = `${this.getSaveKey(slotNumber)}_name`;
    localStorage.setItem(nameKey, name);
  }

  /**
   * Rename a save slot
   */
  renameSave(slotNumber, newName) {
    try {
      const saveKey = this.getSaveKey(slotNumber);
      const savedData = localStorage.getItem(saveKey);
      
      if (!savedData) {
        return { success: false, error: 'No save in this slot' };
      }

      // Update the save data
      const state = JSON.parse(savedData);
      state.slotName = newName;
      localStorage.setItem(saveKey, JSON.stringify(state));
      
      // Update the name key
      this.setSlotName(slotNumber, newName);
      
      return { success: true, slot: slotNumber, slotName: newName };
    } catch (error) {
      console.error('[SaveLoad] Failed to rename save:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Start auto-save timer
   */
  startAutoSave() {
    if (this.autoSaveTimer) {
      return;
    }

    if (AUTO_SAVE_INTERVAL <= 0) return;

    this.autoSaveTimer = setInterval(() => {
      // Silent auto-save (no console log unless there's an error)
      const result = this.save(null, null, true);
      if (!result.success) {
        console.error('[SaveLoad] Auto-save failed:', result.error);
      }
    }, AUTO_SAVE_INTERVAL);
  }

  /**
   * Stop auto-save timer
   */
  stopAutoSave() {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  /**
   * Export save data as JSON string (for backup)
   */
  exportSave(slotNumber = null) {
    try {
      const slot = slotNumber || this.currentSlot;
      const saveKey = this.getSaveKey(slot);
      const savedData = localStorage.getItem(saveKey);
      
      if (!savedData) {
        return null;
      }
      
      return savedData;
    } catch (error) {
      console.error('[SaveLoad] Failed to export save:', error);
      return null;
    }
  }

  /**
   * Import save data from JSON string to a specific slot
   */
  importSave(jsonString, slotNumber = null) {
    try {
      const state = JSON.parse(jsonString); // Validate JSON
      const slot = slotNumber || this.currentSlot;
      const saveKey = this.getSaveKey(slot);
      
      localStorage.setItem(saveKey, jsonString);
      
      if (state.slotName) {
        this.setSlotName(slot, state.slotName);
      }
      
      return { success: true, slot };
    } catch (error) {
      console.error('[SaveLoad] Failed to import save:', error);
      return { success: false, error: error.message };
    }
  }
}

// Singleton instance
const saveLoadManager = new SaveLoadManager();

export default saveLoadManager;
